import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  m,
  useReducedMotion,
  useScroll,
  useSpring,
} from "motion/react";
import {
  HiArrowDown,
  HiArrowRight,
  HiArrowUpRight,
  HiOutlinePause,
  HiOutlinePlay,
} from "react-icons/hi2";
import Home2 from "./Home2";
import CodeWorkspace from "./CodeWorkspace";
import { Reveal } from "./MotionPrimitives";
import "./Home.css";

const ROLES = [
  "Machine Learning Engineer",
  "LLM Specialist",
  "AI Solutions Architect",
  "Deep Learning Practitioner",
];
const STACK = [
  "Python",
  "C++",
  "JavaScript",
  "PyTorch",
  "TensorFlow",
  "HuggingFace",
  "FastAPI",
  "Azure",
];
const intro = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};
const nameIntro = {
  hidden: { y: "105%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

function AnimatedRole({ still }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (still) return;
    const timer = setInterval(
      () => setIndex((value) => (value + 1) % ROLES.length),
      3600,
    );
    return () => clearInterval(timer);
  }, [still]);
  return (
    <div className="home-role" role="group" aria-label={ROLES.join(", ")}>
      <span className="home-role-mark" aria-hidden="true">
        &gt;_
      </span>
      <AnimatePresence mode="wait" initial={false}>
        <m.span
          key={index}
          aria-hidden="true"
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -12, opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {ROLES[index]}
        </m.span>
      </AnimatePresence>
    </div>
  );
}

function HomeContent({ ready }) {
  const reducedMotion = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const still = reducedMotion || paused;
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  return (
    <main className={`home-page${still ? " home-still" : ""}`}>
      <m.div
        className="home-progress"
        style={{ scaleX: progress }}
        aria-hidden="true"
      />
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-grid" aria-hidden="true" />
        <div className="home-aura home-aura-one" aria-hidden="true" />
        <div className="home-aura home-aura-two" aria-hidden="true" />
        <div className="home-shell">
          <div className="home-hero-meta">
            <span>
              <i /> ML Engineer <span className="home-meta-divider">/</span> AI{" "}
              <span className="home-meta-divider">/</span> LLMs
            </span>
            <button
              className="home-motion-toggle"
              onClick={() => setPaused((value) => !value)}
              aria-pressed={paused}
              aria-label={
                reducedMotion
                  ? "Animations reduced by your system preference"
                  : paused
                    ? "Resume ambient animations"
                    : "Pause ambient animations"
              }
              disabled={reducedMotion}
            >
              {still ? <HiOutlinePlay /> : <HiOutlinePause />}{" "}
              {still ? "Motion off" : "Motion on"}
            </button>
          </div>
          <div className="home-hero-layout">
            <m.div
              className="home-hero-copy"
              initial="hidden"
              animate={ready ? "visible" : "hidden"}
              variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
            >
              <m.div className="home-eyebrow" variants={intro}>
                <span className="home-index">01</span> INTRODUCTION{" "}
                <span className="home-hairline" />
              </m.div>
              <h1
                id="home-title"
                className="home-name"
                aria-label="Sep Aminian"
              >
                <span className="home-name-clip">
                  <m.span variants={nameIntro}>
                    Sep
                    <span className="home-name-star" aria-hidden="true">
                      ✳
                    </span>
                  </m.span>
                </span>
                <span className="home-name-clip">
                  <m.span className="home-name-accent" variants={nameIntro}>
                    Aminian<span className="home-name-dot">.</span>
                  </m.span>
                </span>
              </h1>
              <m.div variants={intro}>
                <AnimatedRole still={still} />
              </m.div>
              <m.p className="home-intro" variants={intro}>
                Building production-ready AI systems and Large Language Model
                solutions. Currently at{" "}
                <strong>Carpmaels &amp; Ransford</strong>, London.
              </m.p>
              <m.div className="home-actions" variants={intro}>
                <m.a
                  className="home-button home-button-primary"
                  href="https://www.linkedin.com/in/amirsepehr-aminian/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Get In Touch <HiArrowUpRight />
                </m.a>
                <Link
                  className="home-button home-button-secondary"
                  to="/project"
                >
                  View Work <HiArrowRight />
                </Link>
              </m.div>
            </m.div>
            <m.div
              className="home-workspace-scene"
              initial={{ opacity: 0, y: 35 }}
              animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 35 }}
              transition={{
                duration: 0.8,
                delay: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className="home-orbit" aria-hidden="true">
                <m.div
                  className="home-orbit-ring"
                  animate={{ rotate: still ? 0 : 360 }}
                  transition={
                    still
                      ? { duration: 0 }
                      : { duration: 75, repeat: Infinity, ease: "linear" }
                  }
                >
                  <span />
                  <span />
                </m.div>
                <div className="home-orbit-inner" />
                <span className="home-orbit-cross home-orbit-cross-one">+</span>
                <span className="home-orbit-cross home-orbit-cross-two">+</span>
              </div>
              <div className="home-workspace-label">
                <span>THE WORKSPACE</span>
                <span>〈 / 〉</span>
              </div>
              <CodeWorkspace still={still} />
              <div className="home-workspace-caption">
                <span className="home-caption-line" /> From interface to
                inference.
              </div>
            </m.div>
          </div>
          <div className="home-hero-bottom">
            <div className="home-location">
              <span className="home-location-dot" /> BASED IN LONDON, UK
            </div>
            <a href="#home-about" className="home-scroll-link">
              A little more about me{" "}
              <m.span
                animate={still ? { y: 0 } : { y: [0, 4, 0] }}
                transition={{ duration: 1.8, repeat: Infinity }}
              >
                <HiArrowDown />
              </m.span>
            </a>
          </div>
        </div>
      </section>
      <section
        className="home-impact home-shell"
        aria-label="Experience and impact"
      >
        {[
          { value: "4+", label: "yrs ML/AI", detail: "01 / EXPERIENCE" },
          {
            value: "MSc",
            label: "AI · Distinction",
            detail: "02 / FOUNDATION",
          },
          {
            value: "£2.8m",
            label: "cost savings delivered",
            detail: "03 / IMPACT",
          },
        ].map(({ value, label, detail }, index) => (
          <Reveal key={label} delay={index * 0.09} className="home-stat">
            <span className="home-stat-index">{detail}</span>
            <div className="home-stat-value">
              {value}
              <span aria-hidden="true">↗</span>
            </div>
            <span className="home-stat-label">{label}</span>
          </Reveal>
        ))}
      </section>
      <div
        className="home-stack-ribbon"
        role="img"
        aria-label={`Working with ${STACK.join(", ")}`}
      >
        <div className="home-stack-track" aria-hidden="true">
          {[0, 1].map((copy) => (
            <div className="home-stack-group" key={copy}>
              {STACK.map((tech) => (
                <span key={tech}>
                  <span className="home-stack-star">✳</span>
                  {tech}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <Home2 still={still} />
    </main>
  );
}

export default function Home({ ready = true }) {
  return <HomeContent ready={ready} />;
}
